import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../../../next/CustomDash/filters/definitions/skillTaxonomy';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type SingleValueFilter,
  type StaticListFilter,
} from '../../../next/CustomDash/filters/types/filters';
import {
  PreferenceScope,
  UserPreferenceKey,
  UserPreferenceSubKey,
  type UserPreferences,
} from '../types';

type FilterUpdate = Partial<Filter> & { id: FilterId };

type PreferenceFilterAdapter<K extends UserPreferenceKey> = {
  toPreference: (
    activeFilterMap: FilterSelectionsMap,
    affectedScope: PreferenceScope
  ) => Partial<UserPreferences>;
  toFilter?: (
    value: UserPreferences[K],
    subKey?: UserPreferenceSubKey
  ) => FilterUpdate[];
  relevantFilters?: FilterId[];
};

export const preferenceAdapters: Partial<
  Record<UserPreferenceKey, PreferenceFilterAdapter<UserPreferenceKey>>
> = {
  [UserPreferenceKey.ContingentWorkers]: {
    relevantFilters: [FilterId.CONTINGENT_WORKERS],
    toPreference: (activeFilterMap) => {
      const filter = activeFilterMap.get(FilterId.CONTINGENT_WORKERS);
      const selections = (filter as StaticListFilter)?.selections ?? [];
      if (!selections.length) {
        return {};
      }
      if (selections.some((s) => s.id === 'true')) {
        return { [UserPreferenceKey.ContingentWorkers]: true };
      }
      return { [UserPreferenceKey.ContingentWorkers]: false };
    },
    toFilter: (value) => [
      {
        id: FilterId.CONTINGENT_WORKERS,
        type: FilterType.STATIC_LIST,
        selections: value
          ? [{ id: 'true', name: 'Included' }]
          : [{ id: '', name: 'Excluded' }],
        isUserSubmitted: true,
        isVisible: false,
      },
    ],
  },
  [UserPreferenceKey.Taxonomies]: {
    relevantFilters: [FilterId.ROLE_TAXONOMY, FilterId.SKILL_TAXONOMY],
    toPreference: (activeFilterMap, affectedScope) => {
      const roleFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY) as
        | SingleValueFilter<CustomRoleTaxonomySelection>
        | undefined;
      const skillFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY) as
        | SingleValueFilter<CustomSkillTaxonomySelection>
        | undefined;
      const result: Partial<UserPreferences> = {};

      const taxonomiesResult: Partial<
        UserPreferences[UserPreferenceKey.Taxonomies]
      > = {};

      if (roleFilter && roleFilter.value) {
        const value = roleFilter.value as CustomRoleTaxonomySelection;
        const isDefault = value?.default === true;
        const isRicsTaxonomy = !!(value?.ricsK50Id || value?.ricsK10Id);
        let rolePref: CustomRoleTaxonomySelection | 'adaptive' | undefined;

        const isCompanyAdaptiveMode =
          affectedScope === PreferenceScope.COMPANY &&
          !roleFilter.isUserSubmitted;

        // In COMPANY scope, "Adaptive" is represented by a non-user-submitted role taxonomy filter.
        // The effective value may still be the default taxonomy (fallback), so infer adaptive
        // before mapping explicit defaults.
        if (isCompanyAdaptiveMode && (isRicsTaxonomy || isDefault)) {
          rolePref = 'adaptive';
        } else {
          rolePref = isDefault ? { default: true } : value;
        }

        if (typeof rolePref !== 'undefined') {
          taxonomiesResult.role = rolePref;
          if (rolePref === 'adaptive') {
            taxonomiesResult.effectiveRoleTaxonomySelection = value;
          }
        }
      }

      if (skillFilter && skillFilter.value) {
        const valueObj = skillFilter.value as CustomSkillTaxonomySelection;
        const value = valueObj?.taxonomyId ?? 0;
        const isDefault = value === 0;

        let inferred: number | 'adaptive';
        if (!skillFilter.isUserSubmitted && affectedScope === 'COMPANY') {
          inferred = 'adaptive';
        } else if (isDefault) {
          inferred = 0;
        } else {
          inferred = value;
        }

        taxonomiesResult.skill = inferred;
        if (inferred === 'adaptive') {
          taxonomiesResult.effectiveSkillTaxonomySelection = value;
        }
      }

      if (Object.keys(taxonomiesResult).length > 0) {
        result[UserPreferenceKey.Taxonomies] = taxonomiesResult;
      }

      return result;
    },
    toFilter: (value, subKey) => {
      const updates: FilterUpdate[] = [];
      const composite = value as UserPreferences[UserPreferenceKey.Taxonomies];

      const shouldUpdateRole =
        !subKey || subKey === UserPreferenceSubKey.RoleTaxonomy;
      const shouldUpdateSkill =
        !subKey || subKey === UserPreferenceSubKey.SkillTaxonomy;

      const roleValue = composite?.role;
      if (
        shouldUpdateRole &&
        typeof roleValue !== 'undefined' &&
        roleValue !== 'adaptive'
      ) {
        updates.push({
          id: FilterId.ROLE_TAXONOMY,
          type: FilterType.VALUE,
          value: roleValue as CustomRoleTaxonomySelection,
          isUserSubmitted: true,
        });
      }

      const skillValue = composite?.skill;
      if (
        shouldUpdateSkill &&
        typeof skillValue !== 'undefined' &&
        skillValue !== 'adaptive'
      ) {
        updates.push({
          id: FilterId.SKILL_TAXONOMY,
          type: FilterType.VALUE,
          value: { taxonomyId: skillValue } as CustomSkillTaxonomySelection,
          isUserSubmitted: true,
        });
      }

      return updates;
    },
  },
  [UserPreferenceKey.DataAdjustments]: {
    relevantFilters: [FilterId.DATA_ADJUSTMENTS],
    toPreference: (activeFilterMap) => {
      const filter = activeFilterMap.get(FilterId.DATA_ADJUSTMENTS);
      const selections = (filter as StaticListFilter)?.selections ?? [];
      if (!selections.length) {
        return {};
      }
      if (selections.some((s) => s.id === 'true')) {
        return { [UserPreferenceKey.DataAdjustments]: true };
      }
      return { [UserPreferenceKey.DataAdjustments]: false };
    },
    toFilter: (value) => [
      {
        id: FilterId.DATA_ADJUSTMENTS,
        type: FilterType.STATIC_LIST,
        selections: value
          ? [{ id: 'true', name: 'Unadjusted' }]
          : [{ id: '', name: 'Adjusted' }],
        isUserSubmitted: true,
        isVisible: false,
      },
    ],
  },
};

export const filtersToPreferences = ({
  activeFilterMap,
  affectedScope,
}: {
  activeFilterMap: FilterSelectionsMap;
  affectedScope: PreferenceScope;
}): Partial<UserPreferences> => {
  const result: Partial<UserPreferences> = {};
  (Object.keys(preferenceAdapters) as UserPreferenceKey[]).forEach((key) => {
    const adapter = preferenceAdapters[key];
    if (!adapter) return;
    Object.assign(result, adapter.toPreference(activeFilterMap, affectedScope));
  });
  return result;
};

export const buildFilterUpdatesFromPreferences = (
  preferences: Partial<UserPreferences>,
  subKey?: UserPreferenceSubKey
): FilterUpdate[] => {
  const updates: FilterUpdate[] = [];
  (Object.keys(preferences) as UserPreferenceKey[]).forEach((key) => {
    const value = preferences[key as keyof UserPreferences];
    const adapter = preferenceAdapters[key];
    if (!adapter || typeof value === 'undefined' || !adapter.toFilter) {
      return;
    }
    updates.push(...adapter.toFilter(value, subKey));
  });
  return updates;
};
